import { formatResult } from "helpers/report";
import {
	acreInchPerAcreCell,
	buildFieldSummary,
	buildHeader,
	buildOverallSection,
	getCropPropertyName,
	poundsN20Cell,
	tonnesCO2Cell,
	tonnesCO2PerAcreYearCell,
	tonnesCO2perYearCell,
} from "./shared";

export function buildDetailedReportData(user, reportData, lookupData) {
	const data = [];

	buildHeader(data, user);

	data.push([]);
	data.push([]);

	buildOverallSection(data, reportData);

	data.push([]);
	data.push([]);

	buildPumpingSection(data, user, reportData);

	data.push([]);
	data.push([]);

	buildN2OSection(data, user, reportData, lookupData);

	data.push([]);
	data.push([]);

	buildWaterSection(data, user, reportData);

	data.push([]);
	data.push([]);

	buildFieldSection(data, user);

	return data;
}

function buildPumpingSection(data, user, reportData) {
	data.push([
		{
			value: "Estimated GHG Emission Reductions from Pumping System for Proposed Project",
			fontWeight: "bold",
		},
	]);

	var count = 1;
	for (var result of user.pumpResults) {
		data.push([
			{
				value: "Pump " + count,
				fontWeight: "bold",
			},
		]);

		data.push([
			{
				value: "Pump Name",
			},
			{
				value: formatResult(result.name, true),
			},
		]);

		data.push([
			{
				value: "TDH Pre-project",
			},
			{
				value: formatResult(result.tdhPre, true),
			},
			{
				value: "feet",
			},
		]);

		data.push([
			{
				value: "TDH Post-project",
			},
			{
				value: formatResult(result.tdhPost, true),
			},
			{
				value: "feet",
			},
		]);

		data.push([
			{
				value: "GHG Pre-project",
			},
			{
				value: formatResult(result.ghgPre, true),
			},
			tonnesCO2perYearCell,
		]);

		data.push([
			{
				value: "GHG Post-project Without Fuel Change",
			},
			{
				value: formatResult(result.ghgPumpWithoutFuelChange, true),
			},
			tonnesCO2perYearCell,
		]);

		data.push([
			{
				value: "GHG Post-project With Fuel Conversion Adjustment",
			},
			{
				value: formatResult(result.ghgPumpWithFuelChange, true),
			},
			tonnesCO2perYearCell,
		]);

		data.push([
			{
				value: "GHG Post-project",
			},
			{
				value: formatResult(result.ghgPump, true),
			},
			tonnesCO2perYearCell,
		]);

		data.push([
			{
				value: "Energy Savings from VFD",
			},
			{
				value: formatResult(result.energySavingsVFD, true),
			},
			{
				value: "kWh/hp", //source: combined_watersavings_pumpingGHG_N2O_calculator, Defaults tab, DEER Measure table (should it just be kW/hp?)
			},
		]);

		data.push([
			{
				value: "GHG Savings from VFD",
			},
			{
				value: formatResult(result.ghgSavingsVFD, true),
			},
			tonnesCO2perYearCell,
		]);

		data.push([
			{
				value: "GHG Savings from Renewable Energy",
			},
			{
				value: formatResult(result.ghgSavingsRenewableEnergy, true),
			},
			tonnesCO2perYearCell,
		]);

		data.push([
			{
				value: "GHG Reductions Post-project",
			},
			{
				value: formatResult(result.ghgReductions, true),
			},
			tonnesCO2perYearCell,
		]);

		count++;
	}

	data.push([]);

	data.push([
		{
			value: "Estimated Total GHG Emission Reductions from Pumping System",
			fontWeight: "bold",
		},
	]);

	data.push([
		{
			value: "GHG Benefits per Growing Season",
		},
		{
			value: formatResult(reportData.pumpingReport.yearlyGHGBenefits, true),
		},
		tonnesCO2perYearCell,
	]);

	const tenYearBenefits = reportData.pumpingReport.yearlyGHGBenefits * 10;
	data.push([
		{
			value: "Net GHG Benefits over Useful Life",
		},
		{
			value: formatResult(tenYearBenefits, true),
		},
		tonnesCO2Cell,
	]);

	const totalArea = user.fields.reduce((sum, field) => sum + field.acreage, 0);
	const benefitsPerAcreYear = totalArea != 0 ? reportData.pumpingReport.yearlyGHGBenefits / totalArea : 0;
	data.push([
		{
			value: "GHG Benefits per Acre-Year",
		},
		{
			value: formatResult(benefitsPerAcreYear, true),
		},
		tonnesCO2PerAcreYearCell,
	]);

	const benefitsPerDollar =
		user.fundsRequested && user.fundsRequested > 0 ? formatResult(tenYearBenefits / user.fundsRequested, true) : "N/A";
	data.push([
		{
			value: "Total GHG Emission Reductions per Dollar Requested",
		},
		{
			value: benefitsPerDollar,
		},
		{
			value: "tonnes CO2-eq/$",
		},
	]);
}

function buildN2OSection(data, user, reportData, lookupData) {
	data.push([
		{
			value: "Estimated Nitrous Oxide Emission Reductions for Proposed Project",
			fontWeight: "bold",
		},
	]);

	var fieldCount = 1;
	for (var field of user.fields) {
		data.push([
			{
				value: "Field " + fieldCount,
				fontWeight: "bold",
			},
		]);

		data.push([
			{
				value: "Pre-Project Emissions",
				fontWeight: "bold",
			},
		]);

		buildYearlyN2OResults(data, field, true, lookupData);

		data.push([
			{
				value: "Post-Project Emissions",
				fontWeight: "bold",
			},
		]);

		buildYearlyN2OResults(data, field, false, lookupData);

		data.push([
			{
				value: "Pre-Project Average N2O Emissions",
			},
			{
				value: field.results.beforeN2O,
			},
			tonnesCO2Cell,
		]);

		data.push([
			{
				value: "Post-Project Average N2O Emissions",
			},
			{
				value: field.results.afterN2O,
			},
			tonnesCO2Cell,
		]);

		data.push([
			{
				value: "Average Yearly N2O-N Emission Reductions",
			},
			{
				value: field.results.avgYearlyN2OBenefits,
			},
			tonnesCO2Cell,
		]);

		data.push([
			{
				value: "10-Year Project Benefits",
			},
			{
				value: field.results.tenYearN2OBenefits,
			},
			tonnesCO2Cell,
		]);

		fieldCount++;
	}

	data.push([]);

	data.push([
		{
			value: "Estimated Total N2O Emissions",
			fontWeight: "bold",
		},
	]);

	data.push([
		{
			value: "Pre-Project Average N2O Emissions",
		},
		{
			value: reportData.n2oReport.beforeN2O,
		},
		tonnesCO2Cell,
	]);

	data.push([
		{
			value: "Post-Project Average N2O Emissions",
		},
		{
			value: reportData.n2oReport.afterN2O,
		},
		tonnesCO2Cell,
	]);

	data.push([
		{
			value: "Average Yearly N2O-N Emission Reductions",
		},
		{
			value: reportData.n2oReport.avgYearlyN2OBenefits,
		},
		tonnesCO2Cell,
	]);

	data.push([
		{
			value: "10-Year Project Benefits",
		},
		{
			value: reportData.n2oReport.tenYearN2OBenefits,
		},
		tonnesCO2Cell,
	]);
}

function buildYearlyN2OResults(data, field, isPre, lookupData) {
	var yearCount = 1;
	for (var year of isPre ? field.results.beforeYearlyN2O : field.results.afterYearlyN2O) {
		data.push([
			{
				value: "Year " + yearCount,
			},
		]);

		var cropCount = 1;
		for (var crop of year) {
			var cropPropertyName = getCropPropertyName(yearCount, cropCount, isPre);
			var cropLookupId = (isPre ? field.fielddetails.precropping : field.fielddetails.postcropping)[cropPropertyName];

			if (cropLookupId) {
				data.push([
					{
						value: `Crop ${cropCount} - ${lookupData[parseInt(cropLookupId)].name}`,
					},
					{
						value: crop,
					},
					poundsN20Cell,
				]);
			}

			cropCount++;
		}

		yearCount++;
	}
}

function buildWaterSection(data, user, reportData) {
	data.push([
		{
			value: "Estimated Water Savings for Proposed Project",
			fontWeight: "bold",
		},
	]);

	var fieldCount = 1;
	for (var field of user.fields) {
		data.push([
			{
				value: "Field " + fieldCount,
				fontWeight: "bold",
			},
		]);

		data.push([
			{
				value: "Before Water Use",
			},
			{
				value: field.results.beforeWater,
			},
			acreInchPerAcreCell,
		]);

		data.push([
			{
				value: "After Water Use",
			},
			{
				value: field.results.afterWater,
			},
			acreInchPerAcreCell,
		]);

		data.push([
			{
				value: "Water Savings",
			},
			{
				value: field.results.estWaterSavings,
			},
			acreInchPerAcreCell,
		]);

		data.push([
			{
				value: "Percent Water Savings",
			},
			{
				value: field.results.percentWaterSavings,
			},
			{
				value: "%",
			},
		]);

		fieldCount++;
	}

	data.push([]);

	data.push([
		{
			value: "Field Totals",
			fontWeight: "bold",
		},
	]);

	data.push([
		{
			value: "Before Water Use",
		},
		{
			value: reportData.waterReport.beforeWaterUse,
		},
		acreInchPerAcreCell,
	]);

	data.push([
		{
			value: "After Water Use",
		},
		{
			value: reportData.waterReport.afterWaterUse,
		},
		acreInchPerAcreCell,
	]);

	data.push([
		{
			value: "Water Savings",
		},
		{
			value: reportData.waterReport.waterSavings,
		},
		acreInchPerAcreCell,
	]);

	data.push([
		{
			value: "Percent Water Savings",
		},
		{
			value: reportData.waterReport.percentSavings,
		},
		{
			value: "%",
		},
	]);
}

function buildFieldSection(data, user) {
	data.push([
		{
			value: "SWEEP Project Assessment Tool Field Data Summary",
			fontWeight: "bold",
		},
	]);

	var fieldCount = 1;
	for (var field of user.fields) {
		data.push([
			{
				value: "Field " + fieldCount,
				fontWeight: "bold",
			},
		]);

		buildFieldSummary(data, field);

		fieldCount++;
	}
}
